import * as React from "react";
import {
    Box, Grid, Paper, Stack, Typography, IconButton,
    List, ListItemButton, ListItemText, TextField, Dialog,
    DialogTitle, DialogContent, DialogActions, Tooltip
} from "@mui/material";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { LpField } from "../../../app/components/LpField";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ListAltIcon from "@mui/icons-material/ListAlt";
import SettingsIcon from "@mui/icons-material/Settings";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import { useAppDispatch } from "../../../app/hooks";
import { 
    createSystemModule,
    createSystemProcess,
    createSystemLevelProcess,
    createSystemFunction,
    deleteSystemModule,
    deleteSystemProcess,
    deleteSystemFunction,
    updateSystemModule,
    updateSystemProcess,
    updateSystemFunction
} from "../computerizedSystemsApi";
import { unwrapResult } from "@reduxjs/toolkit";
import type { 
    ComputerizedSystemModule as Module,
    ComputerizedSystemProcess as Process,
    ComputerizedSystemFunction as Functionality
} from "../computerizedSystemsApi";
import { Localized, useLocalization } from "@fluent/react";
import { SaveButton } from "../../../app/components/SaveButton";
import { CancelButton } from "../../../app/components/CancelButton";
import { ProcessEditDialog } from "../dialogs/ProcessEditDialog";


const Section: React.FC<{ title: React.ReactNode; icon?: React.ReactNode; actions?: React.ReactNode; children: React.ReactNode }> = ({ title, icon, actions, children }) => (
    <Paper variant="outlined" sx={{ p: 0, borderRadius: 2 }}>
        <Box
            sx={(t) => ({
                bgcolor: t.palette.mode === "dark" ? t.palette.grey[900] : t.palette.grey[100],
                px: 2,
                py: 1,
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
                borderBottom: `1px solid ${t.palette.divider}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1
            })}
        >
            <Stack direction="row" spacing={1} alignItems="center">
                {icon}
                <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
                {actions}
            </Stack>
        </Box>
        <Box sx={{ p: 1.5 }}>{children}</Box>
    </Paper>
);

const EditDialog: React.FC<{
    open: boolean;
    title: string;
    initial: { name: string; description?: string | null };
    processName?: string;
    onCancel: () => void;
    onSave: (data: { name: string; description: string | null }) => void;
}> = ({ open, title, initial, processName, onCancel, onSave }) => {
    const [name, setName] = React.useState(initial.name);
    const [desc, setDesc] = React.useState(initial.description ?? "");
    const [touched, setTouched] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const RequiredError = () => <Localized id="field-is-required"><span>_Pole jest wymagane</span></Localized>

    const { l10n } = useLocalization()

    React.useEffect(() => {
        if (open) {
            setName(initial.name);
            setDesc(initial.description ?? "");
            setTouched(false);
            setSaving(false);
        }
    }, [open, initial]);

    const nameErr = touched && name.trim() === "";
    const handleSave = async () => {
        setTouched(true);
        if (!name.trim()) return;
        
        setSaving(true);
        try {
            await onSave({ name: name.trim(), description: desc.trim() || null });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
            <DialogTitle 
                    sx={{
                        bgcolor: (theme) => theme.palette.grey[200],
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        py: 0.75, 
                    }}>
                {title}
            </DialogTitle>
            <DialogContent>
                <Stack direction="column" spacing={2} padding={2}>
                    {processName && (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1, p: 1.5, bgcolor: 'info.lighter', borderRadius: 1 }}>
                            <InfoOutlinedIcon fontSize="small" color="info" sx={{ mt: 0.25 }} />
                            <Typography variant="body2" color="text.secondary">
                                {l10n.getString("cs-function-info-process", { processName }, "_This functionality belongs to process: {processName}")}
                            </Typography>
                        </Box>
                    )}
                    <TextField
                        label={l10n.getString("name", null, "_Nazwa")}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        error={nameErr}
                        helperText={nameErr ? <RequiredError /> : undefined}
                        fullWidth
                        disabled={saving}
                    />
                    <TextField
                        label={l10n.getString("cs-optional-description", null, "_Opis (opcjonalnie)")}
                        value={desc}
                        onChange={(e) => setDesc(e.target.value)}
                        fullWidth
                        multiline
                        minRows={2}
                        disabled={saving}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <CancelButton loading={saving} onClick={onCancel} />
                <SaveButton loading={saving} onClick={handleSave} />
            </DialogActions>
        </Dialog>
    );
};

export const SystemComponentsPanel: React.FC<{ 
    system: { id: number; modules?: Module[], processes?: Process[] };
    onReload: () => void;
}> = ({ system, onReload }) => {
    const dispatch = useAppDispatch();
    const { l10n } = useLocalization()

    // Extract all processes from modules and system-level processes
    const allProcesses = React.useMemo(() => {
        const procs: Process[] = [];
        (system?.modules ?? []).forEach(m => {
            (m.processes ?? []).forEach(p => procs.push(p));
        });
        // System-level processes (not in any module)
        (system?.processes ?? []).forEach(p => procs.push(p));
        return procs;
    }, [system?.modules, system?.processes]);
    
    // Create module for system-level processes
    const systemLevelModuleId = -1;
    const systemLevelModule: Module = React.useMemo(() => ({
        id: systemLevelModuleId,
        name: l10n.getString("cs-system-level-processes", null, "_Procesy systemowe (bez modułu)"),
        description: l10n.getString("cs-system-level-processes-description", null, "_Procesy niezwiązane z żadnym modułem"),
        processes: system?.processes ?? []
    }), [system?.processes]);
    
    const allModules = React.useMemo(() => {
        const realModules = system?.modules ?? [];
        const hasSystemLevelProcesses = (system?.processes ?? []).length > 0;
        return hasSystemLevelProcesses ? [systemLevelModule, ...realModules] : realModules;
    }, [system?.modules, system?.processes, systemLevelModule]);
    
    // All modules including system-level module (for dialog use)
    const allModulesWithSystemLevel = React.useMemo(() => {
        const realModules = system?.modules ?? [];
        return [systemLevelModule, ...realModules];
    }, [system?.modules, systemLevelModule]);
    
    const [modules, setModules] = React.useState<Module[]>(allModules);
    const [processes, setProcesses] = React.useState<Process[]>(allProcesses);
    const [selectedModuleId, setSelectedModuleId] = React.useState<number | null>(allModules[0]?.id ?? null);
    const firstProcess = allModules[0]?.processes?.[0];
    const [selectedProcessId, setSelectedProcessId] = React.useState<number | null>(firstProcess?.id ?? null);

    // Sync local state when system prop changes
    React.useEffect(() => {
        setModules(allModules);
        setProcesses(allProcesses);
    }, [allModules, allProcesses]);

    // Dialog state
    const [openMod, setOpenMod] = React.useState<null | { mode: "add" | "edit"; module?: Module }>(null);
    const [openProc, setOpenProc] = React.useState<null | { mode: "add" | "edit"; item?: Process }>(null);
    const [openFunc, setOpenFunc] = React.useState<null | { mode: "add" | "edit"; item?: Functionality }>(null);

    // Get processes for selected module
    const selectedModule = modules.find((m) => m.id === selectedModuleId) || null;
    const modulesProcesses = selectedModule?.processes ?? [];
    
    // Get functions for selected process
    const selectedProcess = modulesProcesses.find((p) => p.id === selectedProcessId) || null;
    const processFunctions = selectedProcess?.functions ?? [];

    // CRUD Module
    const addModule = async (data: { name: string; description: string | null }) => {
        try {
            const result = await dispatch(createSystemModule({
                systemId: system.id,
                module: { name: data.name, description: data.description }
            }));
            const created = unwrapResult(result) as Module;
            onReload();
            setSelectedModuleId(created.id);
            setSelectedProcessId(null);
        } catch (error) {
            console.error("Failed to create module:", error);
        }
    };
    const editModule = async (id: number, data: { name: string; description: string | null }) => {
        if (id === systemLevelModuleId) return;
        try {
            await dispatch(updateSystemModule({
                moduleId: id,
                module: { name: data.name, description: data.description }
            }));
            onReload();
        } catch (error) {
            console.error("Failed to update module:", error);
        }
    };
    const deleteModule = async (id: number) => {
        if (id === systemLevelModuleId) return;
        try {
            await dispatch(deleteSystemModule({ moduleId: id }));
            onReload();
            // Update selection if deleted module was selected
            if (selectedModuleId === id) {
                setSelectedModuleId(allModules[0]?.id ?? null);
                setSelectedProcessId(null);
            }
        } catch (error) {
            console.error("Failed to delete module:", error);
        }
    };

    // CRUD Process
    const addProcess = async (data: { name: string; description: string | null; moduleId: number | null }) => {
        try {
            // If no module selected or system-level module, create system-level process
            if (!data.moduleId || data.moduleId === systemLevelModuleId) {
                const result = await dispatch(createSystemLevelProcess({
                    systemId: system.id,
                    process: { name: data.name, description: data.description }
                }));
                const created = unwrapResult(result) as Process;
                
                onReload();
                setSelectedModuleId(systemLevelModuleId);
                setSelectedProcessId(created.id);
            } else {
                const result = await dispatch(createSystemProcess({
                    systemId: system.id,
                    moduleId: data.moduleId,
                    process: { name: data.name, description: data.description }
                }));
                const created = unwrapResult(result) as Process;
                
                setModules((prev) => prev.map((m) => 
                    m.id === data.moduleId 
                        ? { ...m, processes: [...(m.processes ?? []), created] }
                        : m
                ));
                setSelectedModuleId(data.moduleId);
                setSelectedProcessId(created.id);
            }
        } catch (error) {
            console.error("Failed to create process:", error);
        }
    };
    const editProcess = async (id: number, data: { name: string; description: string | null; moduleId: number | null }) => {
        try {
            await dispatch(updateSystemProcess({
                processId: id,
                process: { 
                    name: data.name, 
                    description: data.description,
                    computerizedSystemId: system.id,
                    moduleId: data.moduleId
                }
            }));
            onReload();
        } catch (error) {
            console.error("Failed to update process:", error);
        }
    };
    const deleteProcess = async (id: number) => {
        try {
            await dispatch(deleteSystemProcess({ processId: id }));
            onReload();
            // Clear selection if deleted process was selected
            if (selectedProcessId === id) {
                setSelectedProcessId(null);
            }
        } catch (error) {
            console.error("Failed to delete process:", error);
        }
    };

    // CRUD Functionality
    const addFunc = async (data: { name: string; description: string | null }) => {
        if (!selectedProcessId) return;
        try {
            const result = await dispatch(createSystemFunction({
                processId: selectedProcessId,
                func: { name: data.name, description: data.description }
            }));
            const created = unwrapResult(result) as Functionality;
            setModules((prev) => prev.map((m) => ({
                ...m,
                processes: (m.processes ?? []).map((p) => 
                    p.id === selectedProcessId
                        ? { ...p, functions: [...(p.functions ?? []), created] }
                        : p
                )
            })));
        } catch (error) {
            console.error("Failed to create function:", error);
        }
    };
    const editFunc = async (id: number, data: { name: string; description: string | null }) => {
        try {
            await dispatch(updateSystemFunction({
                functionId: id,
                func: { name: data.name, description: data.description }
            }));
            onReload();
        } catch (error) {
            console.error("Failed to update function:", error);
        }
    };
    const deleteFunc = async (id: number) => {
        try {
            await dispatch(deleteSystemFunction({ functionId: id }));
            onReload();
        } catch (error) {
            console.error("Failed to delete function:", error);
        }
    };

    return (
        <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
                {/* MODUŁY */}
                <Grid item xs={12} md={4} lg={4}>
                    <Section
                        title= {l10n.getString("cs-modules", null, "_Moduły")}
                        icon={<AccountTreeIcon fontSize="small" />}
                        actions={
                            <Stack direction="row" spacing={1}>
                                <Tooltip title={l10n.getString("cs-add-module", null, "_Dodaj moduł")}>
                                    <IconButton size="small" onClick={() => setOpenMod({ mode: "add" })}><AddIcon fontSize="small" /></IconButton>
                                </Tooltip>
                            </Stack>
                        }
                    >
                        <List dense>
                            {modules.map((m, index) => (
                                <ListItemButton
                                    key={m.id}
                                    selected={m.id === selectedModuleId}
                                    onClick={() => {
                                        setSelectedModuleId(m.id);
                                        setSelectedProcessId(m.processes?.[0]?.id ?? null);
                                    }}
                                    sx={{ borderRadius: 1 }}
                                >
                                    <LpField value={index + 1} sx={{ marginRight: 2 }} />
                                    <ListItemText
                                        primary={m.name}
                                        secondary={m.description || "—"}
                                        primaryTypographyProps={{ fontWeight: m.id === selectedModuleId ? 700 : 500 }}
                                    />
                                    {m.id !== systemLevelModuleId && (
                                        <>
                                            <Tooltip title={l10n.getString("cs-edit-module", null, "_Edytuj moduł")}>
                                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); setSelectedModuleId(m.id); setSelectedProcessId(m.processes?.[0]?.id ?? null); setOpenMod({ mode: "edit", module: m }); }}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={l10n.getString("cs-delete-module", null, "_Usuń moduł")}>
                                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); deleteModule(m.id); }}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    )}
                                </ListItemButton>
                            ))}
                            {modules.length === 0 && (
                                <Typography variant="body2" color="text.secondary">
                                    <Localized id="cs-no-modules">
                                        _Brak modułów.
                                    </Localized>
                                </Typography>
                            )}
                        </List>
                    </Section>
                </Grid>

                {/* PROCESY */}
                <Grid item xs={12} md={4} lg={4}>
                    <Section
                        title={l10n.getString("processes", null, "_Procesy")}
                        icon={<ListAltIcon fontSize="small" />}
                        actions={
                            <Stack direction="row" spacing={1}>
                                <Tooltip title={l10n.getString("cs-add-process", null, "_Dodaj proces")}>
                                    <IconButton size="small" onClick={() => setOpenProc({ mode: "add" })}><AddIcon fontSize="small" /></IconButton>
                                </Tooltip>
                            </Stack>
                        }
                    >
                        {!selectedModule && (
                            <Typography variant="body2" color="text.secondary">
                                <Localized id="cs-process-select">
                                    _Wybierz moduł, aby wyświetlić procesy.
                                </Localized>
                            </Typography>
                        )}
                        {selectedModule && (
                            <List dense>
                                {modulesProcesses.map((p, index) => {
                                    const selected = p.id === selectedProcessId;
                                    return (
                                        <ListItemButton 
                                            key={p.id} 
                                            selected={selected}
                                            onClick={() => setSelectedProcessId(p.id)} 
                                            sx={{ borderRadius: 1 }}
                                        >
                                            <LpField value={index + 1} sx={{ marginRight: 2 }} />
                                            <ListItemText 
                                                primary={p.name} 
                                                secondary={p.description || "—"}
                                                primaryTypographyProps={{ fontWeight: selected ? 700 : 500 }}
                                            />
                                            <Tooltip title={l10n.getString("cs-edit-process", null, "_Edytuj proces")}>
                                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); setSelectedProcessId(p.id); setOpenProc({ mode: "edit", item: p }); }}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={l10n.getString("cs-delete-process", null, "_Usuń proces")}>
                                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); deleteProcess(p.id); }}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </ListItemButton>
                                    );
                                })}
                                {modulesProcesses.length === 0 && 
                                <Typography variant="body2" color="text.secondary">
                                    <Localized id="cs-no-processes-in-module">
                                        _Brak procesów w tym module.
                                    </Localized>
                                </Typography>}
                            </List>
                        )}
                    </Section>
                </Grid>

                {/* FUNKCJONALNOŚCI */}
                <Grid item xs={12} md={4} lg={4}>
                    <Section
                        title={l10n.getString("cs-functions", null, "_Funkcjonalności")}
                        icon={<SettingsIcon fontSize="small" />}
                        actions={
                            <Stack direction="row" spacing={1}>
                                <Tooltip title={l10n.getString("cs-add-functionality", null, "_Dodaj funkcjonalność")}>
                                    <span>
                                        <IconButton size="small" onClick={() => setOpenFunc({ mode: "add" })} disabled={!selectedProcess}><AddIcon fontSize="small" /></IconButton>
                                    </span>
                                </Tooltip>
                            </Stack>
                        }
                    >
                        {!selectedProcess && (
                            <Typography variant="body2" color="text.secondary">
                                <Localized id="cs-functionality-select">
                                    _Wybierz proces, aby wyświetlić funkcjonalności.
                                </Localized>
                            </Typography>
                        )}
                        {selectedProcess && (
                            <List dense>
                                {processFunctions.map((f, index) => {
                                    return (
                                        <ListItemButton key={f.id} sx={{ borderRadius: 1 }}>
                                            <LpField value={index + 1} sx={{ marginRight: 2 }} />
                                            <ListItemText primary={f.name} secondary={f.description || "—"} />
                                            <Tooltip title={l10n.getString("cs-edit-functionality", null, "_Edytuj funkcjonalność")}>
                                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); setOpenFunc({ mode: "edit", item: f }); }}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={l10n.getString("cs-delete-functionality", null, "_Usuń funkcjonalność")}>
                                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); deleteFunc(f.id); }}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </ListItemButton>
                                    );
                                })}
                                {processFunctions.length === 0 && (
                                    <Typography variant="body2" color="text.secondary">
                                        <Localized id="cs-no-functions-in-process">
                                            _Brak funkcjonalności w tym procesie.
                                        </Localized>
                                    </Typography>
                                )}
                            </List>
                        )}
                    </Section>
                </Grid>
            </Grid>

            {/* DIALOGI */}
            <EditDialog
                open={!!openMod}
                title={openMod?.mode === "add" ? l10n.getString("cs-add-module", null, "_Dodaj moduł") : l10n.getString("cs-edit-module", null, "_Edytuj moduł")}
                initial={{ name: openMod?.module?.name ?? "", description: openMod?.module?.description ?? "" }}
                onCancel={() => setOpenMod(null)}
                onSave={(data) => {
                    if (openMod?.mode === "add") addModule(data);
                    else if (openMod?.mode === "edit" && openMod.module) editModule(openMod.module.id, data);
                    setOpenMod(null);
                }}
            />
            <ProcessEditDialog
                open={!!openProc}
                title={openProc?.mode === "add" ? l10n.getString("cs-add-process", null, "_Dodaj proces") : l10n.getString("cs-edit-process", null, "_Edytuj proces")}
                initial={{ 
                    name: openProc?.item?.name ?? "", 
                    description: openProc?.item?.description ?? "",
                    moduleId: openProc?.mode === "edit" ? openProc?.item?.moduleId : selectedModuleId
                }}
                modules={allModulesWithSystemLevel}
                systemLevelModuleId={systemLevelModuleId}
                onCancel={() => setOpenProc(null)}
                onSave={(data) => {
                    if (openProc?.mode === "add") addProcess(data);
                    else if (openProc?.mode === "edit" && openProc.item) editProcess(openProc.item.id, data);
                    setOpenProc(null);
                }}
            />
            <EditDialog
                open={!!openFunc}
                title={openFunc?.mode === "add" ? l10n.getString("cs-add-functionality", null, "_Dodaj funkcjonalność") : l10n.getString("cs-edit-functionality", null, "_Edytuj funkcjonalność")}
                initial={{ name: openFunc?.item?.name ?? "", description: openFunc?.item?.description ?? "" }}
                processName={selectedProcess?.name}
                onCancel={() => setOpenFunc(null)}
                onSave={(data) => {
                    if (openFunc?.mode === "add") addFunc(data);
                    else if (openFunc?.mode === "edit" && openFunc.item) editFunc(openFunc.item.id, data);
                    setOpenFunc(null);
                }}
            />
        </Box>
    );
};
